// utils/i18nUtils.ts - With proper TypeScript definitions
import { createIntl, createIntlCache } from '@formatjs/intl';
import { useSelector } from 'react-redux';

// Cache for better performance
const cache = createIntlCache();

// Define the organization settings interface
export interface OrganizationSettings {
  currency: string;
  timeZone: string;
  locale: string;
  decimalPrecision?: number;
  currencyDisplay?: 'symbol' | 'code' | 'name';
}

// Define the Redux state interface (simplified, adjust based on your actual state)
interface RootState {
  rootReducers?: {
    userData?: {
      userDetails?: {
        organization?: Partial<OrganizationSettings>;
      };
    };
  };
}

/**
 * Get internationalization settings from user's organization
 * @returns {OrganizationSettings} Organization i18n settings
 */
export const useOrganizationSettings = (): OrganizationSettings => {
  const organization = useSelector((state: RootState) => 
    state.rootReducers?.userData?.userDetails?.organization || {}
  );
  
  return {
    currency: organization.currency || 'USD',
    timeZone: organization.timeZone || 'UTC',
    locale: organization.locale || 'en-US',
    decimalPrecision: organization.decimalPrecision || 2,
    currencyDisplay: organization.currencyDisplay || 'symbol',
  };
};

/**
 * Create an intl instance based on locale
 * @param {string} locale - The locale to use
 * @returns {Intl} Intl instance
 */
export const getIntl = (locale = 'en-US') => {
  // We could load translations here if needed
  const messages = {};
  
  const intl = createIntl({
    locale,
    messages,
  }, cache);
  
  return intl;
};

/**
 * Format currency value with enhanced options
 */
export const formatCurrency = (
  amount: number | string | null | undefined, 
  currencyCode: string = 'USD', 
  locale: string = 'en-US',
  options: Intl.NumberFormatOptions = {}
): string => {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }
  
  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    console.warn('formatCurrency: Invalid amount:', amount);
    return '';
  }
  
  try {
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: currencyCode,
      // Allow overriding default options
      maximumFractionDigits: 2,
      ...options
    };
    
    const formatted = new Intl.NumberFormat(locale, formatOptions).format(numAmount);
    return formatted;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${numAmount} ${currencyCode}`;
  }
};

/**
 * Get currency symbol for a given currency code
 */
export const getCurrencySymbol = (
  currencyCode: string = 'USD', 
  locale: string = 'en-US',
  display: 'symbol' | 'code' | 'name' = 'symbol'
): string => {
  try {
    const symbol = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: display,
    })
      .formatToParts(0)
      .find(part => part.type === 'currency')
      ?.value;
    
    return symbol || currencyCode;
  } catch (error) {
    console.error('Error getting currency symbol:', error);
    return currencyCode;
  }
};

/**
 * Format a date in the organization's timezone
 */
export const formatDate = (
  date: Date | string | number | null | undefined,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  timeZone: string = 'UTC',
  locale: string = 'en-US'
): string => {
  if (!date) {
    return '';
  }
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      console.warn('formatDate: Invalid date:', date);
      return '';
    }
    
    const formatted = new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone,
    }).format(dateObj);
    
    return formatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return String(date);
  }
};

/**
 * Calculate price with tax
 */
export const calculatePriceWithTax = (price: number | string, taxRate: number | string): number => {
  const basePrice = Number(price);
  const tax = Number(taxRate);
  
  if (isNaN(basePrice) || isNaN(tax)) {
    console.warn('calculatePriceWithTax: Invalid inputs:', { price, taxRate });
    return 0;
  }
  
  const taxAmount = basePrice * (tax / 100);
  const totalPrice = basePrice + taxAmount;
  
  return totalPrice;
};

/**
 * Format a number
 */
export const formatNumber = (
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
  locale: string = 'en-US'
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const numValue = Number(value);
  if (isNaN(numValue)) {
    console.warn('formatNumber: Invalid number:', value);
    return '';
  }
  
  try {
    const formatted = new Intl.NumberFormat(locale, options).format(numValue);
    return formatted;
  } catch (error) {
    console.error('Error formatting number:', error);
    return String(numValue);
  }
};

/**
 * Get a currency formatter function
 */
export const getCurrencyFormatter = (currencyCode: string = 'USD', locale: string = 'en-US') => {
  return (value: number | string | null | undefined): string => 
    formatCurrency(value, currencyCode, locale);
};

/**
 * Get the display name for a currency code, e.g. "GHS" → "Ghanaian Cedi"
 */
export function getCurrencyName(
  currencyCode: string,
  locale: string = 'en-US'
): string {
  return new Intl.DisplayNames([locale], { type: 'currency' }).of(currencyCode)
         || currencyCode;
}


export default {
  useOrganizationSettings,
  getIntl,
  formatCurrency,
  getCurrencyName, 
  getCurrencySymbol,
  formatDate,
  calculatePriceWithTax,
  formatNumber,
  getCurrencyFormatter
};